import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecurly } from "@recurly/react-recurly";
import "./P1.css";
import {
  AmericanSvg,
  AppleSvg,
  MaterSvg,
  NumberSvg,
  UnionSvg,
  VisaSvg,
} from "../assets/svgs/svg";
import P1Book from "../assets/images/original/p1/p1book.png";
import ApiService from "../service/api";
import { showErrorToast, showSuccessToast } from "../utils/ToastsModal";
import { generateRandomCode } from "../utils/modules";
import { Loader } from "../widgets/Loader";

const P1 = () => {
  const navigate = useNavigate();
  const recurly = useRecurly();
  const risk = recurly.Risk();
  const location = useLocation();

  const specialOfferRef = useRef(null);
  const formRef = useRef();
  const emailRef = useRef();
  const cardNumberRef = useRef();
  const cardMonthRef = useRef();
  const cardYearRef = useRef();
  const cardCvvRef = useRef();
  const cardholderNameRef = useRef();

  const email = new URLSearchParams(location.search).get("email");
  const stateEmail = email || location?.state?.email || "";
  const currentYear = new Date().getFullYear().toString().slice(2, 4);

  // Calculate date 7 days from now
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 7);
  const formattedTrialEndDate = trialEndDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const [userEmail, setUserEmail] = useState(stateEmail);
  const [cardNumber, setCardNumber] = useState("");
  const [cardMonth, setCardMonth] = useState("");
  const [cardYear, setCardYear] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumberError, setCardNumberError] = useState(false);
  const [cardMonthError, setCardMonthError] = useState(false);
  const [cardYearError, setCardYearError] = useState(false);
  const [cardCvvError, setCardCvvError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [activeCtaButton, setActiveCtaButton] = useState(null);
  const [currentPlan, setCurrentPlan] = useState("1-month");
  const [unitPrice, setUnitPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isSupport, setIsSupport] = useState(true);
  const [appleAvailable, setAppleAvailable] = useState("");
  const [isPaymentProcessing, setPaymentProcessing] = useState(false);
  const [gender, setGender] = useState(location?.state?.gender);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 7,
    minutes: 59,
    seconds: 44,
  });

  // Split email into first and last name
  const nameString = userEmail?.split("@")[0];
  const divideStringIntoHalf = () => {
    const length = nameString?.length;
    const middle = Math.floor(length / 2);
    const firstHalf = nameString?.slice(0, middle);
    setFirstName(firstHalf);
    const secondHalf = nameString?.slice(middle);
    setLastName(secondHalf);
  };

  useEffect(() => {
    divideStringIntoHalf();
  }, [userEmail]);

  // Fetch plan prices on mount
  useEffect(() => {
    const fetchPlans = async () => {
      await ApiService.getRecurlyPrice().then(async (response) => {
        if (response.status === 200) {
          const priceList = response?.data?.data?.plan_list;
          const currentUnitPrice =
            priceList?.length > 0 &&
            priceList?.find((plans) => plans.plan_code === currentPlan);
          setUnitPrice(currentUnitPrice?.unit_amount);
          setCurrentPlan(currentUnitPrice?.plan_code);
          setMaxPrice(currentUnitPrice?.max_amount);
        }
      });
    };
    fetchPlans();
  }, []);

  // Countdown timer effect
  useEffect(() => {
    // Set target time: 7 hours, 59 minutes, 44 seconds from now
    const targetTime = new Date();
    targetTime.setHours(targetTime.getHours() + 7);
    targetTime.setMinutes(targetTime.getMinutes() + 59);
    targetTime.setSeconds(targetTime.getSeconds() + 44);

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetTime.getTime() - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCountdown({
          days,
          hours,
          minutes,
          seconds,
        });
      } else {
        // Timer reached zero
        setCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle opening payment form
  const handleOpenPaymentForm = (buttonId) => {
    setActiveCtaButton(buttonId);
    setIsPaymentFormOpen(true);
  };

  // Handle scroll to offer section (now opens form inline)
  const handleScrollToOffer = (buttonId) => {
    setActiveCtaButton(buttonId);
    setIsPaymentFormOpen(true);
  };

  // Handle banner click - scroll to nearest CTA and open form
  const handleBannerClick = () => {
    const currentScrollY = window.scrollY + window.innerHeight / 2;

    // Define all CTA sections with their identifiers and selectors
    const ctaSections = [
      { id: "cta-1", selector: ".cta-section" },
      { id: "cta-2", selector: ".cta-box" },
      { id: "cta-3", selector: ".shouldnt-join-section" },
      { id: "cta-4", selector: ".guarantee-card-section" },
      { id: "special-offer", selector: ".special-offer-section" },
    ];

    // Find the nearest CTA button
    let nearestCta = null;
    let minDistance = Infinity;

    ctaSections.forEach(({ id, selector }) => {
      const element = document.querySelector(selector);
      if (element) {
        const rect = element.getBoundingClientRect();
        const elementY = rect.top + window.scrollY;
        const distance = Math.abs(elementY - currentScrollY);

        if (distance < minDistance) {
          minDistance = distance;
          nearestCta = { id, element };
        }
      }
    });

    // Activate and scroll to the nearest CTA
    if (nearestCta) {
      setActiveCtaButton(nearestCta.id);
      setIsPaymentFormOpen(true);
      setTimeout(() => {
        nearestCta.element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  // Handle close banner
  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  // Handle FAQ toggle
  const handleFaqToggle = (index) => {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  };

  // Card validation handlers
  const handleCardNumberChange = (e) => {
    const inputVal = e.target.value;
    const inputCardNumber = e.target.value.replace(/\D/g, "");
    const formattedCardNumber = inputCardNumber
      .replace(/(\d{4})/g, "$1 ")
      .trim();
    setCardNumber(formattedCardNumber);
    const checkVal = inputVal.split(" ").join("");
    const length = checkVal.length;

    !inputVal || length < 15 || length > 16
      ? setCardNumberError(true)
      : setCardNumberError(false);

    if (length === 16) {
      cardMonthRef.current.focus({ preventScroll: true });
    }
  };

  const handleCardMonthYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length === 0) {
      setCardMonth("");
      setCardYear("");
      setCardMonthError(false);
      setCardYearError(false);
    } else if (value.length <= 2) {
      setCardMonth(value);
      setCardYear(""); // Clear year when backspacing
      setCardMonthError(value.length < 2 || parseInt(value) > 12);
      setCardYearError(false);

      if (value.length === 2 && parseInt(value) <= 12) {
        cardYearRef.current.focus({ preventScroll: true });
      }
    } else if (value.length <= 4) {
      const month = value.slice(0, 2);
      const year = value.slice(2);
      setCardMonth(month);
      setCardYear(year);
      setCardMonthError(month.length < 2 || parseInt(month) > 12);
      setCardYearError(
        year.length < 2 || parseInt(year) < parseInt(currentYear)
      );

      if (year.length === 2 && parseInt(year) >= parseInt(currentYear)) {
        cardCvvRef.current.focus({ preventScroll: true });
      }
    }
  };

  const handleCardYearChange = (e) => {
    const length = e.target.value.length;
    setCardYear(e.target.value);
    length < 2 || e.target.value < currentYear
      ? setCardYearError(true)
      : setCardYearError(false);
    if (length === 2 && e.target.value >= currentYear) {
      cardCvvRef.current.focus({ preventScroll: true });
    }
  };

  const handleCardCvvChange = (e) => {
    const length = e.target.value.length;
    setCardCvv(e.target.value);
    length < 3 ? setCardCvvError(true) : setCardCvvError(false);
    if (length === 3) {
      setCardCvvError(false);
    }
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setUserEmail(email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(!emailRegex.test(email));
  };

  // Handle ConvertKit tags
  const handleTags = () => {
    ApiService.removeTag("4643341", {
      api_secret: process.env.REACT_APP_CK_SECRET_KEY,
      email: userEmail.toLowerCase(),
    }).then(() => {
      ApiService.addTag("4643340", {
        api_secret: process.env.REACT_APP_CK_SECRET_KEY,
        email: userEmail.toLowerCase(),
      });
    });
  };

  // Handle 3D Secure payment
  const handleThreeDSPayment = async (formData) => {
    setLoading(true);
    try {
      await ApiService.createSubscription(formData)
        .then(async (response) => {
          if (response.status === 200) {
            await ApiService.loginUser({
              email: userEmail.toLowerCase(),
            })
              .then((res) => {
                if (res.status === 200) {
                  handleTags();
                  localStorage.setItem("email", userEmail.toLowerCase());
                  showSuccessToast(res?.data?.message);
                  navigate(`/otp`);
                  setLoading(false);
                }
              })
              .catch((err) => {
                showErrorToast(err?.response?.data);
                setLoading(false);
              });
          }
        })
        .catch((error) => {
          showErrorToast(
            error?.response?.data?.error ??
              error?.response?.data?.message ??
              error?.response?.data ??
              "Something went wrong"
          );
          setLoading(false);
        });
    } catch (e) {
      setLoading(false);
    }
  };

  // Handle form submission with Recurly payment
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    let showErr = false;
    if (!recurly && showErr) {
      return;
    }

    let formData = new FormData();
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("number", cardNumber);
    formData.append("month", cardMonth);
    formData.append("year", cardYear);
    formData.append("cvv", cardCvv);
    formData.append("email", userEmail);
    formData.append("three_d_secure_action_required", true);
    formData.append("key", "ewr1-5oFEJbtYWURbdeA3z2DHmJ");

    await ApiService.getRecurlyToken(formData)
      .then((response) => {
        if (
          !cardNumberError &&
          !cardYearError &&
          !cardMonthError &&
          !cardCvvError &&
          !response.message
        ) {
          setLoading(true);
          const code = generateRandomCode();
          const token = response?.data?.id;
          if (token) {
            try {
              ApiService.createSubscription({
                email: userEmail.toLowerCase(),
                token: token,
                code: code,
                plan_code: currentPlan,
                payment_type: "recurly",
              })
                .then(async (response) => {
                  if (response.status === 201) {
                    setLoading(true);
                    const threeDSecure = risk.ThreeDSecure({
                      actionTokenId: response?.data?.action_token_id,
                    });
                    threeDSecure.on("error", (err) => {
                      setLoading(false);
                      console.log("threeDSecure err", err);
                      showErrorToast("Payment Rejected");
                    });

                    threeDSecure.on("token", (threeDsToken) => {
                      setLoading(true);
                      handleThreeDSPayment({
                        email: userEmail.toLowerCase(),
                        token: token,
                        plan_code: currentPlan,
                        three_d_secure_action_result_token_id: threeDsToken?.id,
                        code: code,
                        accountCode: response?.data?.accountCode,
                        accountId: response?.data?.accountId,
                        payment_type: "recurly",
                      });
                    });

                    threeDSecure.attach(document.getElementById("3D-Secure"));
                  }
                  if (response.status === 200) {
                    await ApiService.loginUser({
                      email: userEmail.toLowerCase(),
                    })
                      .then((res) => {
                        if (res.status === 200) {
                          handleTags();
                          localStorage.setItem("email", userEmail);
                          showSuccessToast(res?.data?.message);
                          navigate(`/otp`);
                          setLoading(false);
                        }
                      })
                      .catch((err) => {
                        showErrorToast(err?.response?.data);
                        setLoading(false);
                      });
                  }
                })
                .catch((error) => {
                  showErrorToast(
                    error?.response?.data?.error ??
                      error?.response?.data?.message ??
                      error?.response?.data ??
                      "Something went wrong"
                  );
                  setLoading(false);
                });
            } catch (e) {
              setLoading(false);
              console.log(e.err);
            }
          }
        } else {
          if (
            response?.message ||
            (!cardNumberError &&
              !cardYearError &&
              !cardMonthError &&
              !cardCvvError)
          ) {
            showErrorToast("Card number cannot be a test card number");
          }
          setLoading(false);
        }
      })
      .catch((error) => console.log("error", error));
  };

  // Configure Recurly for Apple Pay
  useEffect(() => {
    const applePayOptions = {
      requiredBillingAddressFields: ["all"],
      requiredShippingContactFields: ["phone", "email", "postalAddress"],
      countryCode: "US",
      recurringPaymentRequest: true,
    };

    recurly.configure({
      applePay: applePayOptions,
    });
  }, [recurly]);

  // Check Apple Pay availability
  useEffect(() => {
    if (!window.ApplePaySession) {
      if (appleAvailable !== "Not Available") {
        setIsSupport(false);
        setAppleAvailable("Not Available");
      }
      return;
    }
  }, [isPaymentProcessing]);

  // Send data for Apple Pay
  const sendData = async (token) => {
    setLoadingPayment(true);
    const code = generateRandomCode();

    try {
      await ApiService.createSubscription({
        email: userEmail.toLowerCase(),
        token: token.id,
        code: code,
        plan_code: currentPlan,
        payment_type: "recurly",
      })
        .then(async (response) => {
          if (response.status === 200) {
            await ApiService.loginUser({
              email: userEmail.toLowerCase(),
            })
              .then((res) => {
                if (res.status === 200) {
                  handleTags();
                  localStorage.setItem("email", userEmail);
                  showSuccessToast(res?.data?.message);
                  navigate(`/otp`);
                  setLoadingPayment(false);
                }
              })
              .catch((err) => {
                showErrorToast(err?.response?.data);
                setLoadingPayment(false);
              });
          }
        })
        .catch((error) => {
          showErrorToast(error?.response?.data);
          setLoadingPayment(false);
        });
    } catch (e) {
      setLoadingPayment(false);
    }
    setPaymentProcessing(false);
    setLoadingPayment(false);
  };

  const endOfTrial = new Date(
    Date.now() + 7 * 24 * 60 * 60 * 1000
  ).toISOString();

  const applePay = recurly.ApplePay({
    country: "US",
    currency: "USD",
    paymentRequest: {
      total: { label: "1-Week Trial", amount: "1.00" },
      recurringPaymentRequest: {
        paymentDescription: "Monthly Subscription",
        regularBilling: {
          label: "Monthly",
          amount: `${maxPrice}`,
          paymentTiming: "recurring",
          recurringPaymentIntervalUnit: "month",
          recurringPaymentIntervalCount: 1,
          recurringPaymentStartDate: endOfTrial,
        },
        trialBilling: {
          label: "1-Week Trial",
          amount: `${unitPrice}`,
          paymentTiming: "recurring",
          recurringPaymentIntervalUnit: "day",
          recurringPaymentIntervalCount: 7,
          recurringPaymentEndDate: endOfTrial,
        },
      },
    },
  });

  // Apple Pay handler with payment processing
  const handleAppleClick = (event) => {
    event.preventDefault();
    if (!isSupport) {
      showErrorToast(
        "Your device or browser is not compatible with Apple Pay."
      );
      return;
    } else if (isSupport && applePay && userEmail) {
      applePay?.ready(function () {
        applePay?.begin();
      });

      applePay.on("error", function (err) {
        console.error("Apple Pay error", err);
      });

      applePay.on("token", async function (token) {
        await sendData(token);
      });
    }
  };

  // Reusable Payment Form Component
  const renderPaymentForm = () => (
    <div
      className="payment-section active"
      style={{ marginTop: "30px", marginBottom: "30px" }}
    >
      <div className="payment-container">
        <div className="offer-card">
          <div className="offer-header">
            <div className="offer-product-preview">
              <img
                src={P1Book}
                alt="AI Creator Course Book"
                className="offer-book-img"
              />
            </div>
            <div className="offer-info">
              <span className="offer-subtitle">Try FreeLanz</span>
              <h2 className="offer-price-title">7-Day Trial for $1</h2>
              <p className="offer-subscription-text">
                Then $39,99 per month starting {formattedTrialEndDate}
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          ref={formRef}
          className="payment-form-body"
        >
          <div className="form-group">
            <label className="field-label">Email</label>
            <input
              ref={emailRef}
              type="email"
              placeholder="email@example.com"
              className={`modern-input ${emailError ? "border-error" : ""}`}
              value={userEmail}
              onChange={handleEmailChange}
              required
            />
            {emailError && (
              <p className="small" style={{ color: "red", marginTop: "5px" }}>
                Enter valid email address
              </p>
            )}
          </div>

          {isPaymentProcessing ? (
            <Loader className="align-self-center mb-4" />
          ) : (
            <button
              type="button"
              className="apple-pay-btn-modern"
              onClick={handleAppleClick}
            >
              <AppleSvg />
            </button>
          )}
          <div className="modern-divider">
            <span>OR</span>
          </div>

          <div className="card-info-box">
            <div className="form-group">
              <label className="field-label">Card information</label>
              <div className="card-number-wrapper">
                <input
                  ref={cardNumberRef}
                  type="tel"
                  placeholder="1234 1234 1234 1234"
                  className={`modern-input card-num-input ${
                    cardNumberError ? "border-error" : ""
                  }`}
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={19}
                  required
                />
                <div className="card-icons">
                  <VisaSvg />
                  <MaterSvg />
                  <AmericanSvg />
                  <UnionSvg />
                </div>
              </div>
            </div>
            <div className="expiry-cvc-row-modern">
              <input
                ref={cardMonthRef}
                type="tel"
                placeholder="MM / YY"
                className={`modern-input expiry-input ${
                  cardMonthError || cardYearError ? "border-error" : ""
                }`}
                value={
                  cardMonth && cardYear
                    ? `${cardMonth} / ${cardYear}`
                    : cardMonth
                }
                onChange={handleCardMonthYearChange}
                inputMode="numeric"
                autoComplete="off"
                maxLength={7}
                required
              />
              <div className="cvc-wrapper">
                <input
                  ref={cardCvvRef}
                  type="tel"
                  placeholder="CVC"
                  className={`modern-input cvc-input ${
                    cardCvvError ? "border-error" : ""
                  }`}
                  value={cardCvv}
                  onChange={handleCardCvvChange}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={4}
                  required
                />
                <div className="d-flex justify-content-center align-items-center cvc-icon-svg">
                  <NumberSvg />
                </div>
              </div>
              <input ref={cardYearRef} type="hidden" value={cardYear} />
            </div>
            <div className="form-group" style={{ marginTop: "20px" }}>
              <label className="field-label">Cardholder name</label>
              <input
                ref={cardholderNameRef}
                type="text"
                placeholder="Full name on card"
                className="modern-input"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                required
              />
            </div>
          </div>

          {isLoading ? (
            <Loader className="align-self-center mb-4" />
          ) : (
            <button type="submit" className="start-trial-btn">
              Start trial
            </button>
          )}
        </form>
        <div
          id="3D-Secure"
          className="threeDSecure"
          style={{
            maxWidth: "390px",
          }}
        ></div>
      </div>
    </div>
  );

  return (
    <div className="p1-container">
      <div className="top-banner">Huge Winter Sale!</div>

      <div className="page-container">
        <header className="hero-header">
          <h1>Master AI Filmmaking In 2 Days</h1>

          <div className="highlights">
            <span className="highlight-yellow">
              Social Media Content, Profitable ads,
            </span>
            <span className="highlight-yellow">
              Short films, and more! No Subscriptions
            </span>
            <span className="highlight-yellow">required.</span>
          </div>
        </header>

        <section className="video-section">
          <div className="video-placeholder">
            <div className="speaker-bg"></div>

            <button className="play-btn">
              <i className="fa-solid fa-play"></i>
            </button>

            <div className="day-badge day-1">
              <span className="day-label">Day</span>
              <span className="day-number">1</span>
            </div>
            <div className="day-badge day-2">
              <span className="day-label">Day</span>
              <span className="day-number">2</span>
            </div>

            <i className="fa-solid fa-volume-high volume-icon"></i>

            <div className="video-controls">
              <span className="time">10:37</span>
              <div className="progress-bar">
                <div className="progress-fill"></div>
                <div className="progress-knob"></div>
              </div>
              <i className="fa-solid fa-ellipsis dots-icon"></i>
            </div>
          </div>
        </section>

        <section className="content-text">
          <p>
            <strong>The AI Creator Course</strong> is for anyone who wants to
            master AI tools (<em>the easy way</em>). Learn how to go viral, grow
            your audience, and scale a business with modern AI.
          </p>
        </section>

        <section className="cta-section">
          <button
            onClick={() => handleScrollToOffer("cta-1")}
            className="green-cta-btn"
          >
            Get Access Today!
          </button>
          {activeCtaButton === "cta-1" &&
            isPaymentFormOpen &&
            renderPaymentForm()}
        </section>

        <section className="sale-details">
          <p>
            <strong>Huge Winter Sale:</strong> Right now you can get the AI
            Creator Course for a one time payment of $98 (68% off 😱){" "}
            <strong>and</strong> you'll get our viral editing course + a pack of
            editing templates!
          </p>
        </section>

        {/* Learning Preview Section */}
        <section className="learning-preview-section">
          <div className="learning-card">
            <h3>No Experience Needed!</h3>
            <h2>Here's What You Will Learn In 2 Days:</h2>
          </div>

          <div className="phase-arrow">
            <i className="fa-solid fa-angles-down"></i>
          </div>
        </section>

        {/* Phase 1 Section */}
        <section className="phase-section">
          <h4 className="phase-label">Phase 1:</h4>
          <h2 className="phase-title">
            The Best
            <br />
            AI Filmmaking Tools
          </h2>
          <div className="phase-highlight">
            <span className="highlight-yellow">
              No expensive subscriptions required!
            </span>
          </div>

          <div className="content-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Hollywood VFX"
                className="phase-img"
              />
            </div>
            <div className="card-body">
              <h3>Hollywood VFX</h3>
              <p>
                In this course, you'll learn how to use powerful AI tools to
                create Hollywood-level visual effects without a big studio
                budget. From explosions and lighting effects to cinematic
                environments and motion graphics, you'll discover how to bring
                your videos to life with stunning visuals that look like they
                belong on the big screen.
              </p>
            </div>
          </div>

          <div className="content-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Character Consistency"
                className="phase-img"
              />
            </div>
            <div className="card-body">
              <h3>Character Consistency</h3>
              <p>
                Consistency is key when building trust and recognition in your
                videos, and that includes your characters. In this course,
                you'll learn how to create and maintain AI-generated characters
                that look and act the same across every scene so your content
                feels polished, professional, and connected.
              </p>
            </div>
          </div>

          <div className="content-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Clone Yourself"
                className="phase-img"
              />
            </div>
            <div className="card-body">
              <h3>Clone Yourself</h3>
              <p>
                You'll also learn how to create AI clones of yourself that can
                speak, move, and deliver content on your behalf. Whether you're
                making tutorials, promos, or social content, these realistic
                digital versions of you make it easy to scale your presence and
                stay on camera without always hitting record.
              </p>
            </div>
          </div>

          <div className="content-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Cinematic Quality"
                className="phase-img"
              />
            </div>
            <div className="card-body">
              <h3>Cinematic Quality</h3>
              <p>
                To create incredible video content, you need AI tools that
                generate stunning, high-impact visuals. In this course, you'll
                discover the top video generators that produce results as good
                as, and often better than, real life.{" "}
                <strong>
                  We also show how to access them WITHOUT needing an expensive
                  subscription
                </strong>
              </p>
            </div>
          </div>
        </section>

        {/* Phase 2: Down Arrow */}
        <div className="phase-arrow main">
          <i className="fa-solid fa-angles-down"></i>
        </div>

        {/* Phase 2 Section */}
        <section className="phase-section">
          <h4 className="phase-label">Phase 2:</h4>
          <h2 className="phase-title">
            How to Grow With AI
            <br />
            Content
          </h2>

          <div className="phase-text-content">
            <p>I'm going to hit you with a harsh reality.</p>
            <p>Content only matters if it's worth watching...</p>
            <p>
              And most people don't know how to create content that's actually
              worth watching.
            </p>
            <p>
              This course will show you the RIGHT way to create content that
              will leave people begging for more.
            </p>
            <p>We break things down into 4 different categories.</p>
          </div>

          <h3 className="category-title">#1 Viral Short Form Content</h3>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Viral Short Form Content"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>Instagram, TikTok, Facebook, Youtube shorts...</p>
              <p>
                These are platforms that complete beginners can go viral on if
                they have the right formula behind their content.
              </p>
              <p>
                Normally it requires expensive cameras, complicated software,
                and days spent behind a computer to get out one quality post.
              </p>
              <p>
                Now you can do it all in a fraction of the time with almost ZERO
                cost using Artificial Intelligence.
              </p>
            </div>
          </div>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x600"
                alt="Viral Content Examples"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>
                Whether you're starting from scratch or supplementing your
                existing content strategy with AI. This course will leave you
                with an endless stream of viral content ideas, and the means to
                turn those ideas into a reality.
              </p>
            </div>
          </div>

          <div className="space-y-4"></div>

          <h3 className="category-title" style={{ marginTop: "40px" }}>
            #2 Youtube Long Form Content
          </h3>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="YouTube Long Form Content"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>
                When I got started on youtube. It would take 1-2 weeks just to
                finish one video and get it posted on youtube.
              </p>
              <p>Today... it's a completely different world.</p>
              <p>
                With AI, we're able to produce professional youtube videos
                faster than ever before, and the results have been off the
                charts.
              </p>
              <p>
                What you see below is the results in our first year of
                implementing AI in our Youtube content strategy
              </p>
            </div>
            <img
              src="https://placehold.co/600x400"
              alt="YouTube Stats"
              className="phase-img"
            />
            <div className="phase-text-content">
              <p>
                In this course we pull back the curtain and show you exactly how
                we're using AI to work smarter, get more views, and grow our
                brand faster than ever before.
              </p>
            </div>
          </div>

          <div className="space-y-4"></div>

          <h3 className="category-title" style={{ marginTop: "40px" }}>
            #3 Profitable Ads
          </h3>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Profitable Ads"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>
                After spending over $275,000 testing AI ads, we learned one
                thing above all else...
              </p>
              <p>AI ads are absolutely CRUSHING it right now.</p>
              <p>
                Time and time again, our top performing advertisements on
                Facebook, instagram, google, and youtube are made with AI.
              </p>
              <p>
                They're performing the ads we spent days painstakingly creating
                with real cameras and complicated editing software.
              </p>
            </div>

            <div className="ads-stats-container">
              <img
                src="https://placehold.co/600x400"
                alt="Ad Stats"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>Why is this happening?</p>
              <p>It's simple...</p>
              <p>
                Because you can create wildly more interesting, funny, and
                engaging ads with AI.
              </p>
              <p>And this is what viewers want.</p>
              <p>
                The world of advertising is changing and if you don't embrace
                these new trends, you'll be left behind wondering why it always
                feels like you're lighting your marketing budget on fire.
              </p>
              <p>
                Oh... and if you're just getting started launching a product or
                business.
              </p>
              <p>
                The best thing you can do (from both a time and money
                perspective) is start with AI ads.
              </p>
            </div>
          </div>

          <div className="space-y-4"></div>

          <h3 className="category-title" style={{ marginTop: "40px" }}>
            #4 Short Films
          </h3>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Short Films"
                className="phase-img"
              />
            </div>
            <div className="phase-text-content">
              <p>
                Are your daydreams filled with incredible ideas for short films?
              </p>
              <p>
                With Artificial intelligence, you can finally bring these ideas
                to life and amaze your friends & family.
              </p>
              <p>
                Every day, AI technology is advancing and this course will
                ensure you stay up to date on latest and greatest techniques to
                create visually stunning, emotionally driven videos.
              </p>
            </div>
          </div>
        </section>

        {/* Phase 3 */}
        <section className="phase-section">
          <div className="phase-arrow">
            <i className="fa-solid fa-angles-down"></i>
          </div>
          <h4 className="phase-label">Phase 3:</h4>
          <h2 className="phase-title">AI Image & Graphic Creation</h2>

          <div className="space-y-4" style={{ height: "20px" }}></div>

          <div className="content-item">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="AI Images 1"
                className="phase-img"
              />
            </div>
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="AI Images 2"
                className="phase-img"
              />
            </div>
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="AI Images 3"
                className="phase-img"
              />
            </div>

            <div className="phase-text-content">
              <p>These images were all made completely with AI.</p>
              <p>It took seconds to generate each one.</p>
              <p>
                And in total it cost me less than 25 cents 😱 <br />
                (no expensive monthly subscription required)
              </p>
            </div>
          </div>

          <div className="imagine-card">
            <h3>
              Imagine being able to create any image you want to use in your
              content.
            </h3>
            <ul className="imagine-list">
              <li>
                <i className="fa-solid fa-xmark"></i> No expensive cameras or
                lighting equipment
              </li>
              <li>
                <i className="fa-solid fa-xmark"></i> No complex, expensive, &
                technical software you need to master
              </li>
              <li>
                <i className="fa-solid fa-xmark"></i> No need to spend HOURS
                trying to get that "perfect" picture.
              </li>
            </ul>
            <p className="imagine-footer">
              The potential is out of this world!
            </p>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Fashion"
              className="phase-img"
            />
            <h3 className="category-label">Fashion</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Wildlife"
              className="phase-img"
            />
            <h3 className="category-label">Wildlife</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Animation"
              className="phase-img"
            />
            <h3 className="category-label">Animation</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Cartoons"
              className="phase-img"
            />
            <h3 className="category-label">Cartoons</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Science Fiction"
              className="phase-img"
            />
            <h3 className="category-label">Science Fiction</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Landscapes"
              className="phase-img"
            />
            <h3 className="category-label">Landscapes</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Logo Design"
              className="phase-img"
            />
            <h3 className="category-label">Logo Design</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Channel Art"
              className="phase-img"
            />
            <h3 className="category-label">Channel Art</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Infographics"
              className="phase-img"
            />
            <h3 className="category-label">Infographics</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Architecture"
              className="phase-img"
            />
            <h3 className="category-label">Architecture</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Health & Wellness"
              className="phase-img"
            />
            <h3 className="category-label">Health & Wellness</h3>
          </div>

          <div className="category-item">
            <img
              src="https://placehold.co/600x400"
              alt="Product Photos"
              className="phase-img"
            />
            <h3 className="category-label">Product Photos</h3>
          </div>

          <hr className="section-divider" />

          <div className="pixar-charactor">
            <h4>You can even turn your furry friend into a Pixar character!</h4>
            <img
              src="https://placehold.co/600x400"
              alt="Pixar Dog 1"
              className="phase-img"
            />
            <img
              src="https://placehold.co/600x400"
              alt="Pixar Dog 2"
              className="phase-img"
            />
            <h4>Or maybe something a bit more fitting for your dog...</h4>
            <img
              src="https://placehold.co/600x400"
              alt="Dog Character"
              className="phase-img"
            />
          </div>

          <h2 className="section-subtitle">
            Skip Photoshop, create incredible
            <br />
            graphics in seconds!
          </h2>

          <div className="imagine-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Professional Logos"
                className="phase-img"
              />
            </div>
            <h3>Professional Logos</h3>
            <p>
              Struggling to come up with a design for your new logo? What if you
              could create 100 unique versions with AI to see what you really
              like? This will make finding that "perfect" logo infinitely
              easier. Not to mention you won't need to hire a professional
              graphic designer.
            </p>
          </div>

          <div className="imagine-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Website Graphics"
                className="phase-img"
              />
            </div>
            <h3>Website Graphics</h3>
            <p>
              The graphic above would normally take me 1-2 hours to create. AI
              made it for me in seconds. So much of this page that you're
              viewing right now was created with AI. This saved me hundreds of
              hours (and thanks to AI, I think this is one of our best looking
              pages ever!)
            </p>
          </div>

          <div className="imagine-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Channel Art"
                className="phase-img"
              />
            </div>
            <h3>Channel Art</h3>
            <p>
              Most beginners spend DAYS stressing over the design for their
              youtube channel art. Most resort to hiring freelancers that cost
              hundreds of dollars. I just used AI to create this banner in
              seconds and it only cost 8 cents... 😱
            </p>
          </div>

          <div className="imagine-card">
            <div className="card-image">
              <img
                src="https://placehold.co/600x400"
                alt="Product Marketing"
                className="phase-img"
              />
            </div>
            <h3>Product Marketing</h3>
            <p>
              When you know the right tools to use. With the right prompt
              architecture, you can create professional facebook & google ads in
              seconds... You can control every element of the image or you can
              let AI do all of the brainstorming!
            </p>
          </div>

          {/* AI Thumbnail Engineer Section */}
          <section className="phase-section no-radius">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h2 className="phase-title">AI Thumbnail Engineer</h2>

            <div className="card-image card-image-large">
              <img
                src="https://placehold.co/600x400"
                alt="AI Thumbnail Revolution"
                className="phase-img"
              />
            </div>

            <div className="content-card">
              <div className="card-body">
                <p className="phase-content-text">
                  To succeed as a content creator,{" "}
                  <strong>you need good thumbnails.</strong>
                </p>
                <p className="phase-content-text">
                  Even if you made an incredible video...
                </p>
                <p className="phase-content-text">
                  <span className="highlight-yellow">
                    Without a good thumbnail, your video will never get clicked
                    and never get viewed.
                  </span>
                </p>
                <p className="phase-content-text">
                  Traditionally, to create a good thumbnail, you'd need an
                  expensive camera, lens, and professional lighting setup.
                </p>
                <p>And that's just to take the PHOTO!</p>
                <p>
                  After that, you'd need to learn the expensive software
                  yourself to add the color grade, the graphics, and any other
                  elements to make it pop
                </p>
                <p>
                  Or... you'd have to hire a professional graphic designer to do
                  that for you, which usually costs between $100-$400
                </p>
              </div>
            </div>

            <h2 className="section-subtitle">
              With AI, you can avoid all of this.
              <br />
              Create Spectacular Thumbnails in seconds!
            </h2>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 1"
                className="thumbnail-img"
              />
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 2"
                className="thumbnail-img"
              />
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 3"
                className="thumbnail-img"
              />
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 4"
                className="thumbnail-img"
              />
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 5"
                className="thumbnail-img"
              />
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="Thumbnail 6"
                className="thumbnail-img"
              />
            </div>

            <div className="content-card">
              <div className="card-body">
                <p className="phase-content-text">
                  Look closely at the AI generated thumbnails above
                </p>
                <p className="phase-content-text">
                  Most of these would have been absolutely impossible to create
                  without AI
                </p>

                <p className="phase-content-text">
                  There's no way I'd ever get in the water with a great white
                  shark...
                </p>
                <p>
                  I don't own any medieval knight armor or a sword (I wish I did
                  😂)
                </p>
                <p>
                  Even the thumbnail showing "economy vs first class flights"
                </p>
                <p>
                  To create that manually, I would need to purchase both an
                  economy and first class flight ticket (thousands of dollars)
                  and THEN I would have also needed to take out my professional
                  cameras in front of all the other passengers and&nbsp;pose
                  like an influencer for 10-30 photos.
                </p>
                <p>
                  <strong>I DO NOT WANT TO DO THAT.</strong>
                </p>
                <p>
                  AI is revolutionizing the world of content creation because it
                  removes the limitations that typically restrict our creative
                  ideas.
                </p>
                <p>
                  Swim with sharks. Travel to outer space. Cast a spell as a
                  wizard.
                </p>
                <p>
                  It's all possible, thanks to Artificial intelligence.&nbsp;
                </p>
                <p className="phase-content-text">
                  <span className="highlight-yellow">
                    And they REALLY work!
                  </span>
                </p>
                <p>
                  The thumbnail below performed extremely well and it was made
                  with AI!
                </p>
              </div>
            </div>

            <div className="category-item">
              <img
                src="https://placehold.co/600x400"
                alt="AI Creator Thumbnail"
                className="thumbnail-img"
              />
            </div>

            <div className="comment-card">
              <img
                src="https://placehold.co/600x400"
                alt="Comments"
                className="thumbnail-img"
              />
            </div>

            <div className="performance-card">
              <h3>AI Thumbnails Perform Extremely Well</h3>
              <p>
                Some people seem to like the AI version of me more than the real
                version... 😅
              </p>
            </div>
          </section>

          {/* Phase 4 Section */}
          <section className="phase-section">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h4 className="phase-label">Phase 4:</h4>
            <h2 className="phase-title">
              AI Content Planning &<br />
              Scriptwriting
            </h2>

            <div className="content-card">
              <div className="card-image card-image-large">
                <img
                  src="https://placehold.co/600x400"
                  alt="AI Content Planning Graphic"
                  className="phase-img"
                />
              </div>
              <div className="card-body">
                <h3 className="card-body-title">AI Content Planning</h3>
                <ul className="check-list">
                  <li>
                    <i className="fa-solid fa-check"></i> Plan an entire month
                    of content in under 30 minutes
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Use AI to
                    reverse-engineer what's working in your niche
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Build a content
                    calendar that actually aligns with your goals
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Organize your ideas
                    and track performance with your personal AI assistant
                  </li>
                </ul>
              </div>
            </div>

            <div className="content-card">
              <div className="card-body">
                <h3 className="card-body-title">AI Scriptwriting</h3>
                <ul className="check-list">
                  <li>
                    <i className="fa-solid fa-check"></i> The exact prompt
                    templates we use to write full YouTube scripts
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Train AI to study your
                    competitors and write like a pro
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Structure videos that
                    retain attention and deliver value fast
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Write for Reels,
                    Shorts, and TikTok using short-form-specific AI workflows
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Phase 5 Section */}
          <section className="phase-section">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h4 className="phase-label">Phase 5:</h4>
            <h2 className="phase-title">AI Studio Upgrade</h2>

            <div className="comparison-container">
              <img
                src="https://placehold.co/600x600"
                alt="Before AI Studio"
                className="thumbnail-img"
              />
              <div className="comparison-label">Before AI</div>
            </div>

            <div className="comparison-container">
              <img
                src="https://placehold.co/600x600"
                alt="After AI Studio"
                className="thumbnail-img"
              />
              <div className="comparison-label">After AI</div>
            </div>

            <div className="content-card">
              <div className="card-body">
                <p className="phase-content-text">
                  When you're just starting, you probably won't have a full
                  content studio.
                </p>
                <p className="phase-content-text">That's fine!</p>
                <p className="phase-content-text">
                  <span className="highlight-yellow">
                    All you need is a blank wall.
                  </span>
                </p>
                <p className="phase-content-text">AI can do the rest!</p>
                <ul className="check-list-green">
                  <li>
                    <i className="fa-solid fa-square-check"></i> Works in any
                    editing program
                  </li>
                  <li>
                    <i className="fa-solid fa-square-check"></i> Works with any
                    camera
                  </li>
                  <li>
                    <i className="fa-solid fa-square-check"></i> Several
                    different AI tools can do this
                  </li>
                  <li>
                    <i className="fa-solid fa-square-check"></i> Customize a new
                    background anytime you film!
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Phase 6 Section */}
          <section className="phase-section">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h4 className="phase-label">Phase 6:</h4>
            <h2 className="phase-title">
              AI Video Editing &<br />
              Enhancement
            </h2>

            <div className="comparison-container">
              <img
                src="https://placehold.co/600x400"
                alt="AI Video Editing Timeline"
                className="thumbnail-img"
              />
              <div className="comparison-label">Ai Video Editing!</div>
            </div>

            <div className="content-card">
              <div className="card-body">
                <img
                  src="https://placehold.co/600x400"
                  alt="Best AI Editing Tools"
                  className="thumbnail-img"
                />
                <h3 className="card-body-title">Best AI Editing Tools</h3>
                <ul className="check-list">
                  <li>
                    <i className="fa-solid fa-check"></i>Edit podcasts in
                    SECONDS!
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Best AI auto caption
                    software
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Structure videos that
                    retain attention and deliver value fast
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> The top 3 FREE tools
                    that will save you hours per week!
                  </li>
                </ul>
              </div>
            </div>

            <div className="content-card">
              <div className="card-body">
                <img
                  src="https://placehold.co/600x400"
                  alt="AI Audio Enhancement"
                  className="thumbnail-img"
                />
                <h3 className="card-body-title">AI Audio Enhancement</h3>
                <ul className="check-list">
                  <li>
                    <i className="fa-solid fa-check"></i>How to quickly improve
                    ANY audio
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i>Turn "cheap"
                    microphones into professional sounding equipment
                  </li>
                  <li>
                    <i className="fa-solid fa-check"></i> Recover audio that's
                    been "ruined"
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Winter Sale Bonuses Section */}
          <section className="bonuses-section">
            <div className="section__overlay"></div>
            <div className="bonuses-content">
              <h1 className="bonuses-title">Winter Sale Bonuses</h1>
              <p className="bonuses-subtitle">
                An extra $196 worth of training & templates
                <br />
                included during this sale!
              </p>

              <div className="bonus-box-container">
                <img
                  src="https://placehold.co/400x500"
                  alt="Viral Video Effects Box"
                  className="bonus-box-img"
                />
              </div>

              <div className="content-card bonus-card">
                <div className="card-body">
                  <h3 className="bonus-card-title">Viral Video Effects</h3>

                  <ul className="check-list-green">
                    <li>
                      <i className="fa-solid fa-check"></i> Learn how to
                      recreate the most viral editing trends!
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Works in Capcut,
                      Premiere Pro, DaVinci, and Final Cut Pro
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Download the footage
                      and follow along, step by step
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Guaranteed to
                      increase views on every video!
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bonus-box-container bonus-spacer">
                <img
                  src="https://placehold.co/400x500"
                  alt="Viral Editing Templates Box"
                  className="bonus-box-img"
                />
              </div>

              <div className="content-card bonus-card">
                <div className="card-body">
                  <h3 className="bonus-card-title">
                    Signature Editing Templates
                  </h3>
                  <ul className="check-list-green">
                    <li>
                      <i className="fa-solid fa-check"></i> Our favorite social
                      media and youtube editing templates!
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Immediately improve
                      the look of your content
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Templates included
                      will work in all editing programs
                    </li>
                    <li>
                      <i className="fa-solid fa-check"></i> Color grading
                      presets, motion backgrounds, sound effects, titles,
                      animations, and more!
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Enroll Today Section */}
          <section className="enroll-section">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>

            <h2 className="enroll-title">Enroll Today For:</h2>

            <div className="pricing-container">
              <span className="price-original">$298</span>
              <span className="price-final">$98</span>
            </div>

            <img
              src="https://placehold.co/600x400"
              alt="The AI Creator Course Bundle"
              className="bundle-img"
            />

            <div className="cta-box">
              <h3 className="cta-box-title">The Ai Creator Course</h3>
              <button
                onClick={() => handleScrollToOffer("cta-2")}
                className="btn-enroll-large"
              >
                ENROLL FOR $98
              </button>
            </div>
            {activeCtaButton === "cta-2" &&
              isPaymentFormOpen &&
              renderPaymentForm()}

            <p className="guarantee-text">
              Don't forget, everything is backed by our
              <br />
              <strong>satisfaction guarantee!</strong>
            </p>

            <div className="secure-checkout-section">
              <i className="fa-solid fa-lock secure-lock"></i>
              <h2 className="secure-checkout-title">Secure & Safe Checkout</h2>
              <div className="payment-logos">
                <img
                  src="https://placehold.co/100x40?text=VISA"
                  alt="Visa"
                  className="payment-logo"
                />
                <img
                  src="https://placehold.co/100x40?text=PayPal"
                  alt="PayPal"
                  className="payment-logo"
                />
                <img
                  src="https://placehold.co/100x40?text=AMEX"
                  alt="American Express"
                  className="payment-logo"
                />
                <img
                  src="https://placehold.co/100x40?text=MC"
                  alt="Mastercard"
                  className="payment-logo"
                />
                <img
                  src="https://placehold.co/100x40?text=DISCOVER"
                  alt="Discover"
                  className="payment-logo"
                />
              </div>
            </div>
          </section>

          {/* Student Testimonials Section */}
          <section className="students-saying-section">
            <div className="phase-arrow">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h2 className="students-saying-title">
              What Students Are Saying...
            </h2>

            <img
              src="https://placehold.co/600x1483"
              alt="Testimonials 1"
              className="bundle-img"
            />
            <img
              src="https://placehold.co/600x1483"
              alt="Testimonials 2"
              className="bundle-img"
            />
          </section>

          {/* Meet Your Instructor Section */}
          <section className="instructor-section">
            <div className="phase-arrow custom">
              <i className="fa-solid fa-angles-down"></i>
            </div>
            <h2 className="instructor-title">Meet Your Instructor</h2>

            <img
              src="https://placehold.co/600x400"
              alt="Anthony Gallo"
              className="instructor-img"
            />

            <p className="instructor-text">
              My name is Anthony Gallo, and I've spent the last 15 years turning
              ideas into videos, and videos into a full-time career — all
              without fancy gear or a film degree.
            </p>
            <p className="instructor-text">
              In high school, I made videos for fun.
            </p>
            <p className="instructor-text">
              In college, while studying to become a doctor, I decided to chase
              my dreams instead, and turned my passion for content into a
              freelance business — working with brands around the world (a
              company I still run today).
            </p>
            <p className="instructor-text">
              In 2019, I co-founded ContentCreator.com by releasing my first
              online course "14 Day Filmmaker"
            </p>
            <p className="instructor-text">
              That course now has 160,000 students from around the globe.
            </p>
            <p className="instructor-text">
              Throughout the process I started a youtube channel & social media
              accounts.
            </p>
            <p className="instructor-text">
              Within the first year of posting content, our Youtube channel hit
              over 100,000 subscribers and across all channels, we have hundreds
              of millions of views focused on one topic:
            </p>
            <p className="instructor-text">
              <strong>
                Providing Content Creators around the world with the best
                education &amp; tools to help them achieve their goals.
              </strong>
            </p>
            <p className="instructor-text">
              Over the past year, the #1 question I get asked is...
            </p>
            <p className="instructor-text">
              <span style={{ backgroundColor: "#ffee00" }}>
                <strong>
                  "Will AI replace content creators as we know it?"
                </strong>
              </span>
            </p>
            <p className="instructor-text">
              <span>And here's the truth:</span>
            </p>
            <p className="instructor-text">
              <span>AI won't replace creators — but creators who&nbsp;</span>
              <span>
                <em>leverage</em>
              </span>
              <span>&nbsp;AI will absolutely replace the ones who don't.</span>
            </p>
            <p className="instructor-text">
              <span>
                AI isn't just hype. It's not a gimmick. It's one of the most
                powerful shifts we've ever seen in content creation — and it's
                already changing the game for those who know how to use it.
              </span>
            </p>
            <p className="instructor-text">
              <span>
                With the right tools and the right strategy, AI gives creators a
                massive advantage:
              </span>
            </p>

            <ul data-spread="false">
              <li>
                <p className="instructor-text">
                  <span>You can focus on the parts you love.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>Outsource the parts you hate.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>Create better content, faster.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>Avoid burnout.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>
                    And unlock a level of creativity that's hard to imagine
                    until you've experienced it yourself.
                  </span>
                </p>
              </li>
            </ul>
            <p className="instructor-text">
              <span>But here's the catch...</span>
            </p>
            <p className="instructor-text">
              <span>
                Most tutorials online are outdated or way too generic. They show
                you what a tool&nbsp;
              </span>
              <span>
                <em>does</em>
              </span>
              <span>&nbsp;— but not how to actually&nbsp;</span>
              <span>
                <em>use</em>
              </span>
              <span>
                &nbsp;it to create viral videos, grow your audience, or scale
                your business.
              </span>
            </p>
            <p className="instructor-text">
              <span>
                When AI first started blowing up, I thought it was a gimmick. A
                shortcut that would just get in the way of learning real skills.
              </span>
            </p>
            <p className="instructor-text">
              <span>And at the time... I wasn't wrong.</span>
            </p>
            <p className="instructor-text">
              <span>
                Two years ago, AI wasn't quite there yet. The tools were
                interesting — but clunky, inconsistent, and not all that helpful
                for creators.
              </span>
            </p>
            <p className="instructor-text">
              <span>
                Still, I kept watching. Because deep down, I knew what was
                coming.
              </span>
            </p>
            <p className="instructor-text">
              <span>
                And sure enough, the pace of progress was INSANE. Within months,
                the tools got smarter. The outputs got better. And suddenly, it
                hit me:
              </span>
            </p>

            <h3>
              <span>
                <strong>
                  AI will become the most valuable tool in a creator's toolkit.
                </strong>
              </span>
            </h3>

            <p className="instructor-text">
              <span>So I dove in.</span>
            </p>
            <p className="instructor-text">
              <span>
                I spent months testing every AI tool I could get my hands on. I
                wasted time. I wasted money. I burned through bad advice and
                outdated tutorials.
              </span>
            </p>
            <p className="instructor-text">
              <span>But eventually, I cracked the code.</span>
            </p>
            <p className="instructor-text">
              <span>Now?</span>
            </p>
            <ul>
              <li>
                <p className="instructor-text">
                  <span>We create 3–4x faster.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>Our videos look better.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>They sound better.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>They perform better.</span>
                </p>
              </li>
              <li>
                <p className="instructor-text">
                  <span>And we're having&nbsp;</span>
                  <span>
                    <em>way more fun</em>
                  </span>
                  <span>&nbsp;making content again.</span>
                </p>
              </li>
            </ul>
            <p className="instructor-text">
              <span>
                If you've ever wondered how your favorite creators crank out
                high-quality content so consistently — this is the secret.
              </span>
            </p>
            <p className="instructor-text">
              <span>And now, I want to share it with you.</span>
            </p>
          </section>

          {/* Is It For Me Section */}
          <section className="for-me-section">
            <div className="phase-arrow main">
              <i className="fa-solid fa-angles-down"></i>
            </div>

            <div className="card-dark-blue">
              <div className="card-body" style={{ padding: "40px 20px" }}>
                <h2 className="card-title-blue">
                  Is The AI Creator Course For Me?
                </h2>
                <p className="card-subtitle-white">
                  We've built this course to be valuable for any stage of your
                  content journey.
                </p>

                <h4 className="list-heading-white">
                  This course is for you if:
                </h4>
                <ul className="check-list-circle-green">
                  <li>
                    <i className="fa-solid fa-circle-check"></i> You're planning
                    to launch a YouTube channel, social media presence, personal
                    brand, or business online
                  </li>
                  <li>
                    <i className="fa-solid fa-circle-check"></i> You want to
                    grow your audience but feel overwhelmed about where to begin
                  </li>
                  <li>
                    <i className="fa-solid fa-circle-check"></i> You're looking
                    to learn the most efficient and optimized strategies to
                    create content with AI
                  </li>
                  <li>
                    <i className="fa-solid fa-circle-check"></i> You work in
                    marketing and want to bring fresh, AI-powered ideas to your
                    team
                  </li>
                  <li>
                    <i className="fa-solid fa-circle-check"></i> You want to
                    future proof yourself to ensure you always stay on top of
                    emerging AI tools and trends
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Shouldn't Join Section */}
          <section className="shouldnt-join-section">
            <div className="card-light-grey">
              <h2 className="card-title-black">
                You Probably Shouldn't Join If...
              </h2>
              <ul className="check-list-circle-red">
                <li>
                  <i className="fa-solid fa-circle-xmark"></i> You hate the
                  thought of posting youtube content and are fine missing out on
                  the insane brand building potential it holds!
                </li>
                <li>
                  <i className="fa-solid fa-circle-xmark"></i> You do not care
                  about SPEED! This course was created for people that want to
                  achieve their goals FAST. If you prefer watching hundreds of
                  repetitive videos on youtube and NOT practicing along the
                  way... then this course is not for you.
                </li>
              </ul>
            </div>

            <div style={{ marginTop: "50px" }}>
              <button
                onClick={() => handleScrollToOffer("cta-3")}
                className="btn-cta-green"
              >
                Get Access Today!
              </button>
              {activeCtaButton === "cta-3" &&
                isPaymentFormOpen &&
                renderPaymentForm()}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <h2 className="faq-title">Frequently Asked Questions</h2>

            <div className="faq-accordion">
              <div
                className={`faq-item ${activeFaqIndex === 0 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(0)}>
                  <span className="faq-question">
                    Do I need to know anything about AI to take this course?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 0 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      {" "}
                      Nope. This course is beginner-friendly and walks you
                      through everything step-by-step, even if you've never used
                      an AI tool before.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 1 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(1)}>
                  <span className="faq-question">
                    What makes this different from all the free YouTube
                    tutorials?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 1 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Most free content is outdated, shallow, or sponsored by
                      tools that don't work. This course shows you exactly how
                      to use the best tools in real-world creator workflows.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 2 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(2)}>
                  <span className="faq-question">
                    How Long Will It Take Me To Complete The Entire Course?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 2 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Luckily - you have options! Remember, you get lifetime
                      access to the course so you can go at whatever pace you
                      find best! We find that most people either:
                    </p>
                    <p>
                      1. <strong>Binger (1-2 Days):</strong> If you're like me
                      and you prefer to binge great content like watching a
                      great Netflix series... Then you can knock the entire
                      course out in 1-2 days but make sure you aren't skipping
                      the practice exercises to develop real skills!
                    </p>
                    <p>
                      2. <strong>Slow &amp; Steady:</strong> If you prefer to
                      take your time and learn at a slower pace, work through a
                      lesson or two a day, along with the practice exercises. It
                      will take you about a week to complete the course at this
                      rate.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 3 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(3)}>
                  <span className="faq-question">
                    Will I need expensive software or gear?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 3 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Nope. Most of the tools we teach are free or extremely
                      affordable, and AI helps you create pro-level content even
                      with minimal equipment.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 4 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(4)}>
                  <span className="faq-question">
                    Is this course only for YouTubers?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 4 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Not at all. While YouTube is a focus, the strategies also
                      apply to short-form content (Reels, TikToks, Shorts),
                      podcasts, business marketing content, and more.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 5 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(5)}>
                  <span className="faq-question">
                    What is ContentCreator.com?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 5 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      ContentCreator.com is the best place to learn content
                      creation skills online. We combine the world's best
                      instructors with the worlds best learning methodology
                      (Apprenticeship Online Learning).
                    </p>
                    <p>
                      Join hundreds of thousands around the world who love
                      ContentCreator.com courses.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 6 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(6)}>
                  <span className="faq-question">
                    What is Apprenticeship-Online Learning?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 6 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Most online courses are surface-level. Instructors speak
                      in abstract language about their process. Or, instructors
                      guide you through overly simplified tutorials that don't
                      match the real world. As a result, these courses can often
                      feel shallow or disappointing.
                      <br />
                      ContentCreator.com courses are different. We are
                      pioneering a new way to learn online called
                      Apprenticeship-Online Learning.
                    </p>
                    <p>
                      In this approach, you learn like an apprentice, watching
                      experts do what they do best. Your instructor brings you
                      into their behind-the-scenes creative process from start
                      to finish, explaining everything they are doing and
                      thinking along the way.
                    </p>
                    <p>
                      You get to (virtually) sit next to them in their studio
                      and see everything -- every idea, decision, setback, tool,
                      and technique -- out in the open, for real. All recorded
                      as high-quality, on-demand video, allowing you to
                      apprentice with the world's best anytime, anywhere.
                    </p>
                    <p>
                      Apprenticeship (working and learning alongside an expert)
                      has always been the best way to learn. It just hasn't been
                      accessible to most people, until now.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 7 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(7)}>
                  <span className="faq-question">
                    Can I Take This Course From Anywhere?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 7 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>
                      Yes, the course is completely online so you can take the
                      course from anywhere using any device. Learn from the
                      comfort of your own home or take your course with you on
                      the go.
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`faq-item ${activeFaqIndex === 8 ? "active" : ""}`}
              >
                <div className="faq-header" onClick={() => handleFaqToggle(8)}>
                  <span className="faq-question">
                    Do I Get Lifetime Access To The Course?
                  </span>
                  <span className="faq-plus">+</span>
                </div>
                {activeFaqIndex === 8 && (
                  <div className="faq-answer" style={{ display: "block" }}>
                    <p>How does lifetime access sound?</p>
                    <p>
                      After enrolling you have unlimited access to this program
                      for as long as you like. You can go at your own pace.
                    </p>
                    <p>
                      If you purchased the course but forgot your login info,
                      simply email support@contentcreator.com and we'll be happy
                      to help you!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Satisfaction Guarantee Section */}
          <section className="guarantee-card-section">
            <div className="card-light-blue">
              <img
                src="https://placehold.co/280x280"
                alt="100% Satisfaction Guarantee"
                className="guarantee-badge"
              />
              <h3 className="guarantee-heading">Is there a guarantee?</h3>
              <p className="guarantee-text">
                Absolutely! Our products are built on customer satisfaction. If
                you're not satisfied, send us an email within our 7 day refund
                policy and we'll send you your money right back, no questions
                asked.
              </p>
            </div>
            <img
              style={{ margin: "30px auto" }}
              src="https://placehold.co/280x280"
              alt="Guarantee Badge"
              className="guarantee-badge"
            />
            <div style={{ marginTop: "20px" }}>
              <button
                onClick={() => handleScrollToOffer("cta-4")}
                className="btn-cta-green"
              >
                Click Here To Enroll Today!
              </button>
              {activeCtaButton === "cta-4" &&
                isPaymentFormOpen &&
                renderPaymentForm()}
            </div>
          </section>

          {/* Special Offer Section */}
          {/* <div className="special-offer-section" ref={specialOfferRef}>
            <div className="text-center">
              {!(activeCtaButton === 'special-offer' && isPaymentFormOpen) && (
                <>
                  <div className="offer-card">
                    <div className="offer-header">
                      <div className="offer-product-preview">
                        <img src={P1Book} alt="AI Creator Course Book" className="offer-book-img" />
                      </div>
                      <div className="offer-info">
                        <span className="offer-subtitle">Try FreeLanz</span>
                        <h2 className="offer-price-title">7-Day Trial for $1</h2>
                        <p className="offer-subscription-text">Then $39,99 per month starting {formattedTrialEndDate}</p>
                      </div>
                    </div>
                  </div>

                  <button className="get-plan-btn" onClick={() => handleOpenPaymentForm('special-offer')}>
                    Get My Plan
                  </button>
                </>
              )}
              {activeCtaButton === 'special-offer' && isPaymentFormOpen && renderPaymentForm()}
            </div>
          </div> */}
        </section>

        {/* Footer */}
        <footer>
          <div className="footer-container">
            <p className="footer-copyright">
              © 2025 WEBFY CORP LTD By visiting this page, you agree to{" "}
              <a href="#">terms and conditions</a>,{" "}
              <a href="#">Privacy Policy</a> &{" "}
              <a href="#">earnings disclaimer.</a>
            </p>

            <p className="footer-disclaimer">
              This site is not a part of the Facebook website or Facebook Inc.
              Additionally, This site is NOT endorsed by Facebook in any way.
              FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>

            <p className="footer-disclaimer">
              DISCLAIMER: The sales figures stated on this landing page and
              discussed in the AI Creator Course program are our personal sales
              figures and in some cases the sales figures of previous or
              existing clients. Please understand these results are not typical.
              We're not implying you'll duplicate them (or do anything for that
              matter). The average person who buys "how to" information gets
              little to no results. We're using these references for example
              purposes only. Your results will vary and depend on many factors
              including but not limited to your background, experience, and work
              ethic. All business entails risk as well as massive and consistent
              effort and action. If you're not willing to accept that, please DO
              NOT PURCHASE THE AI CREATOR COURSE.
            </p>
          </div>
        </footer>

        {/* Sticky Footer Banner */}
        {isBannerVisible && (
          <div className="sticky-footer-banner">
            <button className="banner-close" onClick={handleCloseBanner}>
              &times;
            </button>
            <div className="banner-container">
              <div className="countdown-timer">
                <div className="timer-block">
                  <span className="timer-number" id="days">
                    {String(countdown.days).padStart(2, "0")}
                  </span>
                  <span className="timer-label">DAY</span>
                </div>
                <div className="timer-colon">:</div>
                <div className="timer-block">
                  <span className="timer-number" id="hours">
                    {String(countdown.hours).padStart(2, "0")}
                  </span>
                  <span className="timer-label">HRS</span>
                </div>
                <div className="timer-colon">:</div>
                <div className="timer-block">
                  <span className="timer-number" id="minutes">
                    {String(countdown.minutes).padStart(2, "0")}
                  </span>
                  <span className="timer-label">MIN</span>
                </div>
                <div className="timer-colon">:</div>
                <div className="timer-block">
                  <span className="timer-number" id="seconds">
                    {String(countdown.seconds).padStart(2, "0")}
                  </span>
                  <span className="timer-label">SEC</span>
                </div>
              </div>
              <button onClick={handleBannerClick} className="banner-btn">
                Enroll Today
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default P1;
